import { FC, useCallback, useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Redirect } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  GET_CLIENT_LEVEL_DASHBOARD,
  GetClientLevelDashboardVariables,
  GetClientLevelDashboardResponse
} from 'share/queries/cases'
import {
  getInactiveActiveCase,
  setActiveCase
} from 'reducers/activecase/activecaseSlice'
import { clearActiveCourse } from 'reducers/activecourse/activecourseSlice'
import { decrementCaseCount } from 'reducers/authentication/authenticationSlice'
import { formatDate } from 'share/utils/utils'
import StyledAccordion from 'components/molecules/StyledAccordion'
import DeactivateCaseModal from 'components/features/dashboard/DeactivateCaseModal'
import {
  MutationCaseResponse,
  DeactivateCaseVariables,
  DEACTIVATE_CASE
} from 'share/mutations/cases'
import DeactivateCasesModal from 'components/features/dashboard/DeactivateCasesModal'
import ContentNotAvailable from 'components/atoms/ContentNotAvailable'
import { AccountCreationStep } from 'share/utils/utils'
import { logOut } from 'share/utils/authenticationUtils'

import {
  CardsWrapper,
  ClientLevelDashContainer,
  ShowDeactivatedBtnContainer
} from './styles'

const ClientLevelDashboard: FC = () => {
  const authentication = useAppSelector((state) => state.authentication)
  const dispatch = useAppDispatch()
  const [caseId, setCaseId] = useState(0)
  const [toCase, setToCase] = useState(false)
  const [showDeactivation, setShowDeactivation] = useState(false)
  const [showDeactivatedList, setShowDeactivatedList] = useState(false)
  const [getActiveCases, { loading, data, error }] = useLazyQuery<
    GetClientLevelDashboardResponse,
    GetClientLevelDashboardVariables
  >(GET_CLIENT_LEVEL_DASHBOARD, { variables: { active: true } })
  const [deactivateCase] =
    useMutation<MutationCaseResponse, DeactivateCaseVariables>(DEACTIVATE_CASE)
  const [getInactiveCases, { data: dataInactiveCases }] = useLazyQuery<
    GetClientLevelDashboardResponse,
    GetClientLevelDashboardVariables
  >(GET_CLIENT_LEVEL_DASHBOARD, { variables: { active: false } })

  useEffect(() => {
    if (authentication) {
      if (authentication.account) {
        if (
          authentication.account.accountCreationStep !=
          AccountCreationStep.CompletedAllSteps
        ) {
          logOut(dispatch)
        }
      }
    }
  }, [authentication])

  useEffect(() => {
    getActiveCases()
    getInactiveCases()
    const nonActiveCase = getInactiveActiveCase()
    dispatch(setActiveCase(nonActiveCase))
    dispatch(clearActiveCourse())
  }, [])

  const onDeactivateCaseClick = useCallback((caseId: number) => {
    setCaseId(caseId)
    setShowDeactivation(true)
  }, [])

  const handleCloseDeactivation = useCallback(() => {
    setShowDeactivation(false)
  }, [])

  const handleCloseDeactivatedList = useCallback(() => {
    setShowDeactivatedList(false)
  }, [])

  const handleDeactivation = async (deactivationReason: string) => {
    const responseMutation = await deactivateCase({
      variables: { id: caseId, reason: deactivationReason },
      refetchQueries: [
        { query: GET_CLIENT_LEVEL_DASHBOARD, variables: { active: true } }
      ]
    })

    setShowDeactivation(false)
    dispatch(decrementCaseCount)
    if (!responseMutation) {
      console.log('Error trying to deactivate case ' + caseId)
    }
  }

  function NoCasesRefreshClick() {
    window.location.reload()
  }

  if (loading) return <div></div>
  if (error) return <div>Error ...</div>

  if (toCase) return <Redirect to={`/dashboard/case/${caseId}`} />

  return (
    <ClientLevelDashContainer>
      <h2>Welcome back, {authentication?.account?.givenName}</h2>
      <>
        <CardsWrapper>
          {data?.cases?.clientLevelDashboard?.clientDashboardItemList.map(
            (clientDashboardItem, index) => (
              <Card key={clientDashboardItem.id}>
                <div className='row text-left'>
                  <div className='col-12'>
                    <h3 data-testid={`cases-case-type-${index}`}>
                      {clientDashboardItem.caseType}
                    </h3>
                  </div>
                </div>

                <div className='row text-left'>
                  <div className='col-12'>
                    <h6 data-testid={`cases-case-lasted-visited-${index}`}>
                      Last visited:{' '}
                      {formatDate(
                        clientDashboardItem.lastVisitedDate
                          ? clientDashboardItem.lastVisitedDate
                          : ''
                      )}
                    </h6>
                  </div>
                </div>

                <div className='row mb-3 text-left'>
                  <div className='col-12'>
                    <Badge
                      pill
                      variant='secondary mr-2'
                      data-testid={`cases-case-take-course-badge-${index}`}
                    >
                      Take Course
                    </Badge>
                    <Badge
                      pill
                      variant='secondary'
                      data-testid={`cases-case-documents-required-badge-${index}`}
                    >
                      Documents Required
                    </Badge>
                  </div>
                </div>

                <StyledAccordion>
                  <div className='row'>
                    <div className='col-12  text-left'>
                      <label className='font-weight-bold mr-1'>Status:</label>
                      <label data-testid={`cases-case-status-${index}`}>
                        {clientDashboardItem.status + ' | '}
                        {clientDashboardItem.status === 'Pre-Filing'
                          ? 'Case Not Filed'
                          : clientDashboardItem.caseNumber}
                      </label>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-12  text-left'>
                      <label className='font-weight-bold mr-1'>Attorney:</label>
                      <label data-testid={`cases-case-attorney-${index}`}>
                        {clientDashboardItem.attorneyName}
                      </label>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-12  text-left'>
                      <label className='font-weight-bold mr-1'>Firm:</label>
                      <label data-testid={`cases-case-firm-${index}`}>
                        {clientDashboardItem.firmName}
                      </label>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-12  text-left'>
                      <label className='font-weight-bold mr-1'>
                        Case Opened:
                      </label>
                      <label data-testid={`cases-case-opened-date-${index}`}>
                        {clientDashboardItem.openDate
                          ? formatDate(clientDashboardItem.openDate)
                          : ''}
                      </label>
                    </div>
                  </div>

                  <div className='row mb-3'>
                    <div className='col-12 text-left'>
                      <Button
                        data-testid={`cases-case-deactivate-link-${index}`}
                        variant='link'
                        onClick={() =>
                          onDeactivateCaseClick(clientDashboardItem.id)
                        }
                        className='cases-deactivate-case'
                      >
                        Deactivate Case
                      </Button>
                    </div>
                  </div>
                </StyledAccordion>
                <Button
                  data-testid={`cases-case-goto-casedashboard-button-${index}`}
                  className='ui-go-to-case-btn'
                  onClick={() => {
                    setCaseId(clientDashboardItem.id)
                    setToCase(true)
                  }}
                >
                  Go to Case Dashboard
                </Button>
              </Card>
            )
          )}
        </CardsWrapper>
        <DeactivateCaseModal
          show={showDeactivation}
          onDeactivate={handleDeactivation}
          onHide={handleCloseDeactivation}
        />
      </>
      {data?.cases?.clientLevelDashboard?.clientDashboardItemList?.length ===
        0 && (
        <>
          <ContentNotAvailable
            iconClass='mdi-gavel'
            datatestid='cases-no-active-cases-message-wrapper'
          >
            <div className='cases-no-active-cases-message-wrapper'>
              <div
                data-testid='cases-no-active-cases-message'
                className='mb-3 mx-auto cases-no-active-cases-message'
              >
                We are still preparing your case data. Please try refreshing the
                page.
              </div>
              <Button
                variant='primary'
                onClick={NoCasesRefreshClick}
                data-testid='no-cases-retry-button'
                className='retry-btn'
              >
                Retry
              </Button>
            </div>
          </ContentNotAvailable>
        </>
      )}
      {dataInactiveCases?.cases?.clientLevelDashboard?.clientDashboardItemList
        ?.length !== 0 && (
        <ShowDeactivatedBtnContainer>
          <Button
            data-testid='cases-show-deactived-cases-button'
            variant='link'
            onClick={() => setShowDeactivatedList(true)}
          >
            Show Deactivated Cases
          </Button>
        </ShowDeactivatedBtnContainer>
      )}
      <DeactivateCasesModal
        matterList={
          dataInactiveCases?.cases?.clientLevelDashboard
            ?.clientDashboardItemList
        }
        show={showDeactivatedList}
        onHide={handleCloseDeactivatedList}
      />
    </ClientLevelDashContainer>
  )
}

export default ClientLevelDashboard
